import type { TodoTask } from '@/types/todo';
import { getTodayDateString } from './dateHelpers';

export const toDateOnly = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  return value.slice(0, 10);
};

const getTodayString = () => getTodayDateString();

const compareDates = (a: string | null, b: string | null) => {
  if (!a || !b) return 0;
  if (a === b) return 0;
  return a > b ? 1 : -1;
};

const isBefore = (a: string | null, b: string | null) => {
  if (!a || !b) return false;
  return compareDates(a, b) < 0;
};

export const isTaskOpen = (task: TodoTask) => task.status === 'open';

export const isTaskSomeday = (task: TodoTask) => Boolean(task.isSomeday);

export const getTodayTasks = (tasks: TodoTask[], todayOrString?: string) => {
  const today = todayOrString ?? getTodayString();
  return tasks.filter((task) => {
    if (!isTaskOpen(task) || task.status === 'deleted' || isTaskSomeday(task)) {
      return false;
    }
    const start = toDateOnly(task.startDate ?? task.scheduledDate);
    const due = toDateOnly(task.dueDate);
    if (!start && !due) {
      return false;
    }
    if (start && compareDates(start, today) <= 0) {
      return true;
    }
    if (due && compareDates(due, today) <= 0) {
      return true;
    }
    return false;
  });
};

export const getUpcomingTasks = (tasks: TodoTask[], todayOrString?: string) => {
  const today = todayOrString ?? getTodayString();
  return tasks.filter((task) => {
    if (!isTaskOpen(task) || task.status === 'deleted' || isTaskSomeday(task)) {
      return false;
    }
    const start = toDateOnly(task.startDate ?? task.scheduledDate);
    const due = toDateOnly(task.dueDate);
    if (!start && !due) {
      return false;
    }
    if (start && compareDates(start, today) > 0) {
      return true;
    }
    if (due && compareDates(due, today) > 0) {
      return true;
    }
    return false;
  });
};

export const getAnytimeTasks = (tasks: TodoTask[]) =>
  tasks.filter(
    (task) =>
      isTaskOpen(task) &&
      !isTaskSomeday(task) &&
      !task.startDate &&
      !task.scheduledDate &&
      !task.dueDate &&
      task.status !== 'deleted'
  );

export const getSomedayTasks = (tasks: TodoTask[]) =>
  tasks.filter((task) => isTaskOpen(task) && isTaskSomeday(task) && task.status !== 'deleted');

export const getLogbookTasks = (tasks: TodoTask[]) =>
  tasks
    .filter((task) => task.status === 'completed' || task.status === 'cancelled')
    .sort((a, b) => {
      const aDate = a.completedAt || a.cancelledAt || a.updatedAt || '';
      const bDate = b.completedAt || b.cancelledAt || b.updatedAt || '';
      return bDate.localeCompare(aDate);
    });

export const getTrashTasks = (tasks: TodoTask[]) =>
  tasks
    .filter((task) => task.status === 'deleted')
    .sort((a, b) => (b.deletedAt || '').localeCompare(a.deletedAt || ''));

export const groupUpcomingTasks = (tasks: TodoTask[], todayOrString?: string) => {
  const today = todayOrString ?? getTodayString();
  const buckets: Record<string, TodoTask[]> = {};
  tasks.forEach((task) => {
    const start = toDateOnly(task.startDate ?? task.scheduledDate);
    const due = toDateOnly(task.dueDate);
    const primary = start && compareDates(start, today) > 0 ? start : due || start;
    const key = primary || 'undated';
    if (!buckets[key]) {
      buckets[key] = [];
    }
    buckets[key].push(task);
  });
  return buckets;
};

export const isOverdue = (task: TodoTask, todayOrString?: string) => {
  const today = todayOrString ?? getTodayString();
  const due = toDateOnly(task.dueDate);
  const start = toDateOnly(task.startDate ?? task.scheduledDate);
  if (due && isBefore(due, today)) {
    return true;
  }
  if (start && isBefore(start, today)) {
    return true;
  }
  return false;
};
